import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';
import DashboardLayout from './components/DashboardLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import AuditLogsPage from './pages/AuditLogsPage';
import StudentsPage from './pages/StudentsPage';
import StudentDetailPage from './pages/StudentDetailPage';
import ClassesPage from './pages/ClassesPage';
import ScreeningsPage from './pages/ScreeningsPage';
import ResourcesPage from './pages/ResourcesPage';
import CasesPage from './pages/CasesPage';
import ParentDashboard from './pages/ParentDashboard';
import ParentEngagementPage from './pages/ParentEngagementPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import MessagesPage from './pages/MessagesPage';
import CoachingPage from './pages/CoachingPage';
import SuperadminPage from './pages/SuperadminPage';
import SettingsPage from './pages/SettingsPage';
import {
  ReportsPage,
} from './pages/PlaceholderPages';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Check for session_id in URL hash before rendering routes
const AppRouter = () => {
  const location = useLocation();
  
  // Synchronously check for session_id to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Protected routes with DashboardLayout */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/students" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StudentsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/students/:studentId" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StudentDetailPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/classes" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ClassesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/screenings" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ScreeningsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/resources" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ResourcesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/cases" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CasesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/parent" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ParentDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/parent-engagement" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ParentEngagementPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/announcements" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AnnouncementsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MessagesPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/coaching" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CoachingPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/superadmin" 
        element={
          <ProtectedRoute allowedRoles={['superadmin']}>
            <DashboardLayout>
              <SuperadminPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ReportsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      {/* Admin routes */}
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute allowedRoles={['tenant_owner', 'center_director']}>
            <DashboardLayout>
              <UsersPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/settings" 
        element={
          <ProtectedRoute allowedRoles={['tenant_owner']}>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/audit-logs" 
        element={
          <ProtectedRoute allowedRoles={['tenant_owner', 'center_director']}>
            <DashboardLayout>
              <AuditLogsPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
      
      {/* Catch-all redirect to dashboard */}
      <Route 
        path="*" 
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
